import type {
  AuditListResponse,
  AuthResponse,
  CreateInviteRequest,
  CreateInviteResponse,
  HealthResponse,
  InviteList,
  InvitePublic,
  LoginRequest,
  RefreshRequest,
  RegisterRequest,
  RegistrationPending,
  TokenPair,
  User,
  UserPublic,
} from '@osd/shared-types';

const BASE = '/api';
const ACCESS_KEY = 'osd.auth.access';
const REFRESH_KEY = 'osd.auth.refresh';

/**
 * Thin typed fetch wrapper. Errors throw an `ApiError` with status + body.
 *
 * If `Authorization` is set on `init.headers`, it's used as-is. Otherwise
 * the auth token from `localStorage['osd.auth.access']` is appended
 * (when present). This keeps the call sites clean — you don't have to
 * thread the token through every request.
 *
 * Refresh-on-401 (root/osd#2):
 *   When the server returns 401 for an authenticated request, we try
 *   `/api/auth/refresh` once and retry the original request with the
 *   new access token. Concurrent 401s share a single in-flight refresh
 *   promise so only one POST /api/auth/refresh runs at a time across
 *   parallel request failures. On refresh failure, we notify the
 *   AuthProvider via `onUnauthorized` so it can clear local state and
 *   the user gets a clean re-login prompt.
 *
 *   Public endpoints (`/api/auth/login`, `/api/auth/refresh`,
 *   `/api/auth/register`, `/api/auth/logout`) bypass the refresh loop
 *   — a 401 from those is the actual answer (bad credentials, expired
 *   refresh, etc.), not a stale access token.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Path patterns (relative to BASE='/api', so they DO NOT carry the
 * leading '/api' prefix that `request()` strips before passing to
 * the URL builder). Matches '/auth/login', '/auth/refresh', etc.
 * — the actual paths the request wrapper sees.
 */
const PUBLIC_PATH_PATTERNS: RegExp[] = [/^\/auth\/(?:login|refresh|register|logout)\/?$/];

/**
 * Callback AuthProvider registers so the api layer can ask it to
 * clear local state when refresh has definitively failed (e.g.
 * refresh token revoked, user disabled).
 *
 * Single subscriber is enough — AuthProvider is the only state owner.
 */
let onUnauthorized: (() => void) | null = null;

/**
 * Register the unauthorized callback. Called by AuthProvider on mount;
 * the callback is invoked when a 401 + refresh retry fails.
 */
export function setUnauthorizedHandler(handler: () => void): () => void {
  onUnauthorized = handler;
  return () => {
    if (onUnauthorized === handler) onUnauthorized = null;
  };
}

/**
 * Module-local refresh coordinator. Concurrent 401s share one in-flight
 * POST /api/auth/refresh; later callers await the same promise and
 * reuse the new access token for their retry. If refresh fails, all
 * waiters see the same error and notify onUnauthorized once.
 */
let inflightRefresh: Promise<TokenPair> | null = null;

async function refreshAccessToken(): Promise<TokenPair> {
  if (inflightRefresh !== null) return inflightRefresh;
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) throw new ApiError('No refresh token', 401, null);
  inflightRefresh = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken } satisfies RefreshRequest),
      });
      const text = await res.text();
      const body = text ? (JSON.parse(text) as unknown) : null;
      if (!res.ok) {
        throw new ApiError(`HTTP ${res.status} ${res.statusText}`, res.status, body);
      }
      const tokens = body as TokenPair;
      localStorage.setItem(ACCESS_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
      return tokens;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

function isPublicPath(path: string): boolean {
  return PUBLIC_PATH_PATTERNS.some((re) => re.test(path));
}

/**
 * Public for tests + the AuthProvider's probe (/api/auth/me).
 * NOT public for the rest of the app — use `api.*` instead.
 *
 * @param retried  `true` on the second call after a successful refresh;
 *                 if THAT call also 401s we throw the original ApiError
 *                 so the caller surfaces it (no infinite loop).
 */
async function request<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Authorization')) {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token !== null) headers.set('Authorization', `Bearer ${token}`);
  }
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Body is not JSON (e.g. an nginx 502 page). Surface the raw
      // text as the body so the caller can still inspect it via
      // ApiError.body without losing information.
      body = text;
    }
  }
  if (res.ok) return body as T;

  const err = new ApiError(`HTTP ${res.status} ${res.statusText}`, res.status, body);

  // Refresh-on-401: only for authenticated, non-public requests, and
  // only once (retried === false). The recursive retry sets retried=true
  // so if THAT also 401s we throw — no infinite loop.
  if (
    res.status === 401 &&
    retried === false &&
    !isPublicPath(path) &&
    headers.has('Authorization') &&
    localStorage.getItem(REFRESH_KEY) !== null
  ) {
    try {
      await refreshAccessToken();
      return request<T>(path, init, true);
    } catch {
      // Refresh definitively failed (expired/revoked refresh token, user
      // disabled, network error). Notify AuthProvider to clear state and
      // surface the original 401 to the caller. The refresh error is
      // intentionally swallowed — `err` carries the more useful HTTP status
      // for the caller (typically the original 401).
      if (onUnauthorized !== null) onUnauthorized();
      throw err;
    }
  }

  throw err;
}

export const api = {
  // Hit the JSON health endpoint, not the SPA fallback. In production
  // nginx serves /health as index.html, so polling that URL returns
  // HTML → r.json() throws → the footer reports "unreachable" forever
  // (root/osd#54).
  health: () => fetch('/api/health').then((r) => r.json() as Promise<HealthResponse>),
  users: {
    list: () => request<{ users: User[]; count: number }>('/users'),
    get: (id: string) => request<User>(`/users/${id}`),
  },
  /**
   * Auth surface — note the function-call shape (`api.auth()`) so the
   * caller gets a fresh closure with the current `request()` bindings
   * and we keep the tree-shakeable module exports.
   */
  auth: () => ({
    login: (body: LoginRequest): Promise<AuthResponse> =>
      request<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    register: (body: RegisterRequest): Promise<AuthResponse | RegistrationPending> =>
      request<AuthResponse | RegistrationPending>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /**
     * `refresh()` bypasses the refresh-on-401 loop by definition — calling
     * `/api/auth/refresh` directly. Exposed so callers (e.g. tests) can
     * force a refresh without going through the 401 path.
     */
    refresh: async (body: RefreshRequest): Promise<TokenPair> => {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      const parsed = text ? (JSON.parse(text) as unknown) : null;
      if (!res.ok) {
        throw new ApiError(`HTTP ${res.status} ${res.statusText}`, res.status, parsed);
      }
      return parsed as TokenPair;
    },
    logout: (): Promise<void> => request<void>('/auth/logout', { method: 'POST' }),
    me: (): Promise<{ user: UserPublic }> => request<{ user: UserPublic }>('/auth/me'),
  }),

  admin: () => ({
    listPending: (): Promise<{ pending: User[]; count: number }> =>
      request<{ pending: User[]; count: number }>('/admin/approvals'),
    approve: (id: string): Promise<{ user: User }> =>
      request<{ user: User }>(`/admin/approvals/${id}/approve`, {
        method: 'POST',
      }),
    reject: (id: string): Promise<void> =>
      request<void>(`/admin/approvals/${id}/reject`, {
        method: 'POST',
      }),
    /**
     * Audit log — admin only. `filter` translates to query params;
     * `undefined` keys are omitted so the server's defaults apply.
     */
    listAudit: (filter?: {
      target?: string;
      actor?: string;
      action?: string;
      since?: string;
      until?: string;
      limit?: number;
    }): Promise<AuditListResponse> => {
      const params = new URLSearchParams();
      if (filter?.target) params.set('target', filter.target);
      if (filter?.actor) params.set('actor', filter.actor);
      if (filter?.action) params.set('action', filter.action);
      if (filter?.since) params.set('since', filter.since);
      if (filter?.until) params.set('until', filter.until);
      if (filter?.limit !== undefined) params.set('limit', String(filter.limit));
      const qs = params.toString();
      const suffix = qs ? `?${qs}` : '';
      return request<AuditListResponse>(`/admin/audit${suffix}`);
    },
    listUserAudit: (userId: string): Promise<AuditListResponse> =>
      request<AuditListResponse>(`/admin/users/${userId}/audit`),

    /**
     * Invite tokens (root/osd#27) — admin-only CRUD.
     * `createInvite` is the ONLY place the plaintext token surfaces
     * in the UI; pass it straight to a "copy to clipboard" button.
     */
    listInvites: (): Promise<InviteList> => request<InviteList>('/admin/invites'),
    createInvite: (body: CreateInviteRequest): Promise<CreateInviteResponse> =>
      request<CreateInviteResponse>('/admin/invites', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    revokeInvite: (id: InvitePublic['id']): Promise<void> =>
      request<void>(`/admin/invites/${id}`, { method: 'DELETE' }),
  }),
};
